import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import clsx from 'clsx';
import RowContainer from './components/rowContainer';
import {useTranslation} from 'react-i18next';
import {getConfig} from '../../../util/configUtil';
import React, {useEffect, useState} from 'react';
import {useTimelineStyles} from './useTimelineStyles';
import moment from 'moment';
import {startCase} from 'lodash';
import API from '../../../app/api';
import {setSnackbar} from '../../../store/reducers/globals';
import {useDispatch} from 'react-redux';
import AgyleLoadingSpinner from '../../other/agyleLoadingSpinner';
import {Box} from '@mui/material';
import {getConsultantName} from '../../../pages/admit/admitUtils';
import {getAppointmentSlots} from '../../../store/reducers/appointments';
import {APPOINTMENT_BOOK_TYPES} from '../../../pages/ward/map/patientList/utils/edReviewUtils';

const AppointmentTimelineHistory = ({appointment, expanded}) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    // Config
    const dateFormat = getConfig('defaultDateTimeFormat');

    const classes = useTimelineStyles();
    const duration = moment.duration(moment(appointment?.endDate).diff(moment(appointment?.startDate))).humanize();

    const [sessionDetails, setSessionDetails] = useState(null);
    const [sessionDetailsLoading, setSessionDetailsLoading] = useState(false);

    useEffect(() => {
        if (appointment?.assignedSession) {
            const abortController = new AbortController();

            const fetchSession = async () => {
                setSessionDetailsLoading(true);
                try {
                    const session = await API.getAppointmentSession(appointment?.assignedSession, abortController.signal);

                    if (session) setSessionDetails(session);
                } catch (err) {
                    dispatch(setSnackbar({type: 'error', message: 'Error getting assigned slot\'s session details.'}));
                } finally {
                    setSessionDetailsLoading(false);
                }
            };

            fetchSession();

            return () => abortController.abort();
        }
    }, [appointment?.assignedSession]);

    const getAppointmentType = () => {
        if (appointment?.appointmentTye) {
            switch (appointment?.appointmentTye) {
                case APPOINTMENT_BOOK_TYPES.SDEC:
                    return t('SDEC continuation');
                case APPOINTMENT_BOOK_TYPES.BEDAS:
                    return t('Follow-up');
                case APPOINTMENT_BOOK_TYPES.NONE:
                default:
                    return t('Regular appointment');
            }
        }
        return t('Regular appointment');
    };

    return (
        <Timeline className={classes.root}>
            <TimelineItem style={{marginTop: 10}}>
                <TimelineSeparator>
                    <TimelineDot
                        variant="filled"
                        className={clsx(classes.dot)}
                    />

                    <TimelineConnector className={classes.connector}/>
                </TimelineSeparator>

                <TimelineContent
                    className={clsx(classes.content)}
                    style={{marginBottom: expanded ? 0 : 0}}
                >
                    <RowContainer>
                        {t('Start Date')}: {moment(appointment?.startDate).format(dateFormat)} ({duration})
                    </RowContainer>
                </TimelineContent>
            </TimelineItem>

            {expanded &&
                <>
                    <TimelineItem>
                        <TimelineSeparator className={classes.tinySeparator}>
                            <TimelineDot className={clsx(classes.dot, 'small')} variant="filled"/>
                            <TimelineConnector className={classes.connector}/>
                        </TimelineSeparator>

                        <TimelineContent className={clsx(classes.content, 'margin')}>
                            <RowContainer>
                                {t('Reason')}: {appointment.description}
                            </RowContainer>
                        </TimelineContent>
                    </TimelineItem>

                    <TimelineItem>
                        <TimelineSeparator className={classes.tinySeparator}>
                            <TimelineDot className={clsx(classes.dot, 'small')} variant="filled"/>
                        </TimelineSeparator>

                        <TimelineContent className={clsx(classes.content, 'margin')}>
                            <RowContainer>
                                {t('Status')}: {startCase(appointment.status)}
                            </RowContainer>
                        </TimelineContent>
                    </TimelineItem>

                    {sessionDetailsLoading && (
                        <Box sx={{ml: 4}}>
                            <AgyleLoadingSpinner label={t('Loading Session Details...')}/>
                        </Box>
                    )}


                    {!sessionDetailsLoading && (
                        <>
                            <TimelineItem>
                                <TimelineSeparator className={classes.tinySeparator}>
                                    <TimelineDot className={clsx(classes.dot, 'small')} variant="filled"/>
                                    <TimelineConnector className={classes.connector}/>
                                </TimelineSeparator>

                                <TimelineContent className={clsx(classes.content, 'margin')}>
                                    <RowContainer>
                                        {t('Session Name')}: {sessionDetails?.sessionName || 'N/D'}
                                    </RowContainer>
                                </TimelineContent>
                            </TimelineItem>

                            <TimelineItem>
                                <TimelineSeparator className={classes.tinySeparator}>
                                    <TimelineDot className={clsx(classes.dot, 'small')} variant="filled"/>
                                    <TimelineConnector className={classes.connector}/>
                                </TimelineSeparator>

                                <TimelineContent className={clsx(classes.content, 'margin')}>
                                    <RowContainer>
                                        {t('Clinic')}: {sessionDetails?.clinic?.description || 'N/D'}
                                    </RowContainer>
                                </TimelineContent>
                            </TimelineItem>

                            <TimelineItem>
                                <TimelineSeparator className={classes.tinySeparator}>
                                    <TimelineDot className={clsx(classes.dot, 'small')} variant="filled"/>
                                    <TimelineConnector className={classes.connector}/>
                                </TimelineSeparator>

                                <TimelineContent className={clsx(classes.content, 'margin')}>
                                    <RowContainer>
                                        {t('Consultant')}: {sessionDetails?.consultant ? getConsultantName(sessionDetails?.consultant) : 'N/D'}
                                    </RowContainer>
                                </TimelineContent>
                            </TimelineItem>

                            <TimelineItem>
                                <TimelineSeparator className={classes.tinySeparator}>
                                    <TimelineDot className={clsx(classes.dot, 'small')} variant="filled"/>
                                    <TimelineConnector className={classes.connector}/>
                                </TimelineSeparator>

                                <TimelineContent className={clsx(classes.content, 'margin')}>
                                    <RowContainer>
                                        {t('Appointment Type')}: {getAppointmentType()}
                                    </RowContainer>
                                </TimelineContent>
                            </TimelineItem>

                            <TimelineItem>
                                <TimelineSeparator>
                                    <TimelineDot className={classes.dot} variant="outlined"/>
                                </TimelineSeparator>

                                <TimelineContent className={clsx(classes.content, 'margin')}>
                                    <RowContainer>
                                        {t('Specialty')}: {sessionDetails?.specialty?.__description || 'N/D'}
                                    </RowContainer>
                                </TimelineContent>
                            </TimelineItem>
                        </>
                    )}
                </>
            }

            {!expanded && ((
                <TimelineItem>
                    <TimelineSeparator>
                        <TimelineDot className={classes.dot} variant="outlined"/>
                    </TimelineSeparator>

                    <TimelineContent className={classes.fullWidthContent}>
                        <RowContainer>
                            {t('Status')}: {startCase(appointment.status)}
                        </RowContainer>
                    </TimelineContent>
                </TimelineItem>
            ))}
        </Timeline>
    );
};
export default AppointmentTimelineHistory;